import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  DollarSign,
  Download,
  Edit,
  Eye,
  Mail,
  MapPin,
  Package,
  Phone,
  Plus,
  Trash2,
  Truck,
  User,
} from "lucide-react";
import { FC, useMemo, useState } from "react";
import { TClient } from "~/types";
import { capitalize, getClientName } from "~/utils";
import {
  ClientStatus,
  ClientType,
  InventoryStatusCode,
  JobStatus,
  Role,
  ServiceType,
} from "~/utils/constants";
import { useClientStore } from "./clients.store";
import { useGetInventories } from "~/services/inventory";
import { Alert, Button, Modal, Spin, Table, Tag, Tabs, Typography } from "antd";
import { TableInventory } from "../../inventories/list-inventories/table-inventory";
import { useBooking } from "~/store";
import { useCreateNote } from "~/services/note";
import { useNotification } from "~/hooks";
import dayjs from "dayjs";
import { useGetClient } from "~/services/client";
import { useExportInventory } from "~/services/inventory";
import { useGetClientBookings } from "~/services/booking";
import { useInventoryStore } from "../../inventories";
import { useDeleteClient } from "~/services/client/client.mutations";
import { useNavigate } from "react-router-dom";
import { useAuth } from "~/store";
import { UseMutationResult } from "@tanstack/react-query";

const { Text } = Typography;

type Props = { client: TClient };

export const DetailClient: FC<Props> = ({ client }) => {
  const [expandedClient, setExpandedClient] = useState<string | null>();
  return (
    <div
      key={client.id}
      className="bg-white rounded-lg border overflow-hidden shadow-sm"
    >
      {/* Client Header */}
      <div
        className="p-4 border-b flex justify-between items-center cursor-pointer hover:bg-gray-50"
        onClick={() =>
          setExpandedClient(expandedClient === client.id ? null : client.id)
        }
      >
        <div className="flex items-center space-x-3">
          {expandedClient === client.id ? (
            <ChevronDown size={20} />
          ) : (
            <ChevronRight size={20} />
          )}
          <div>
            <div className="flex items-center space-x-2">
              <span className="font-medium">{client.clientRef}</span>
              {getClientTypeBadge(client.type)}
              {getStatusBadge(client.status)}
            </div>
            <div className="font-medium text-lg mt-1">{client.companyName}</div>
          </div>
        </div>

        <div className="flex flex-col items-end">
          <div className="flex items-center space-x-2 text-gray-600 text-sm">
            <User size={14} />
            <span className="mt-1">{getClientName(client)}</span>
          </div>
          <div className="flex items-center space-x-2 text-gray-600 text-sm mt-1">
            <Mail size={14} />
            <span>{client.email}</span>
            <span className="mx-1">•</span>
            <Phone size={14} />
            <span>{client.contactPhone}</span>
          </div>
        </div>
      </div>

      {/* Client Details */}
      {expandedClient === client.id && (
        <ExpandedDetails key={client.id} clientId={client.id} />
      )}
    </div>
  );
};

const ExpandedDetails = ({ clientId }: { clientId: string }) => {
  const {
    data: client,
    isLoading,
    isRefetching,
    refetch,
  } = useGetClient(clientId);

  // Fetch inventory separately for counts (fetch more to check for active items)
  const { data: inventoryData } = useGetInventories(
    { clientId, page: 1, pageSize: 100 }, // Fetch enough to check for active inventory
    { enabled: !!clientId }
  );

  const { user } = useAuth(); // Get current user
  const isSuperAdmin = user?.role === Role.SUPER_ADMIN;

  const { openClientModal, openProfileModal } = useClientStore();
  const { openBookingModal } = useBooking();
  const { success, error } = useNotification();
  const exportMutation = useExportInventory();
  const navigate = useNavigate();

  const [selectedClient, setSelectedClient] = useState<TClient | null>(null);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);

  const { mutate: createNote, isPending: isCreatingNote } = useCreateNote();
  const deleteMutation = useDeleteClient();

  const handleExportClientInventory = () => {
    if (!client) return;

    exportMutation.mutate(
      { clientId: client.id },
      {
        onSuccess: () => {
          success({
            message: "Export successful",
            description: `Inventory data for ${
              client.companyName || getClientName(client)
            } has been exported to CSV`,
          });
        },
        onError: (err) => {
          error({
            message: "Export failed",
            description:
              err.message || "There was an error exporting the inventory data",
          });
        },
      }
    );
  };

  const handleAddNote = () => {
    if (!noteContent.trim() || !client) return;

    createNote(
      {
        clientId: client.id,
        content: noteContent.trim(),
      },
      {
        onSuccess: () => {
          success({
            message: "Note added successfully",
          });
          setNoteContent("");
          setShowNoteForm(false);
          refetch();
        },
        onError: (err) => {
          error({
            message: "Failed to add note",
            description: err.message,
          });
        },
      }
    );
  };

  const handleDeleteClient = () => {
    if (!client) return;

    setIsDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    if (!client) return;

    deleteMutation.mutate(client.id, {
      onSuccess: (response) => {
        setIsDeleteModalVisible(false);
        success({
          message: "Client deleted",
          description:
            response?.message ||
            `${
              client.companyName || getClientName(client)
            } has been deleted successfully`,
        });
        navigate("/clients/list");
      },
      onError: (err: any) => {
        setIsDeleteModalVisible(false);
        error({
          message: "Delete failed",
          description: err.message || "Failed to delete client",
        });
      },
    });
  };

  return (
    <Spin spinning={isLoading || isRefetching} size="large">
      <div className="divide-y">
        {/* Client Summary */}
        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Contact Information */}
          <div>
            <h3 className="font-medium mb-3">Contact Information</h3>
            <div className="bg-gray-50 rounded-lg border p-3">
              <div className="mb-2">
                <div className="flex items-center space-x-2 text-gray-600 text-sm mb-1">
                  <User size={14} />
                  <span>Primary Contact</span>
                </div>
                <div>
                  {client?.firstName} {client?.lastName}
                </div>
                {/* <div className="text-sm text-gray-500">
                    {client?.contact.position}
                  </div> */}
              </div>
              <div className="mb-2">
                <div className="flex items-center space-x-2 text-gray-600 text-sm mb-1">
                  <Mail size={14} />
                  <span>Email</span>
                </div>
                <div>{client?.email}</div>
              </div>
              <div className="mb-2">
                <div className="flex items-center space-x-2 text-gray-600 text-sm mb-1">
                  <Phone size={14} />
                  <span>Phone</span>
                </div>
                <div>{client?.contactPhone}</div>
              </div>
              <div>
                <div className="flex items-center space-x-2 text-gray-600 text-sm mb-1">
                  <MapPin size={14} />
                  <span>Address</span>
                </div>
                <div>
                  {client?.shippingAddresses?.[0]?.address ||
                   client?.billingAddress?.address ||
                   'No address available'}
                </div>
              </div>
            </div>
          </div>

          {/* Inventory Summary */}
          {inventoryData && inventoryData.meta.totalItems > 0 && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium">Inventory Summary</h3>
                <button className="text-sm text-blue-600 hover:text-blue-800">
                  View All
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg border p-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">
                      Total Items
                    </div>
                    <div className="font-medium text-xl">
                      {inventoryData.meta.totalItems}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">In Storage</div>
                    <div className="font-medium text-xl">
                      {inventoryData.items?.filter(
                        (inv) =>
                          inv.status === InventoryStatusCode.AVAILABLE_ON_HAND
                      ).length ?? 0}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">On Loan</div>
                    <div className="font-medium text-xl">
                      {/* TODO: Add status filter when available */}0
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">In Transit</div>
                    <div className="font-medium text-xl">
                      {/* TODO: Add status filter when available */}0
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Activity Summary */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium">Activity Summary</h3>
              <button
                className="text-sm text-blue-600 hover:text-blue-800"
                onClick={() => setShowActivityModal(true)}
              >
                View All
              </button>
            </div>
            <div className="bg-gray-50 rounded-lg border p-3">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <Calendar size={16} className="text-gray-400" />
                    <span>Total Bookings</span>
                  </div>
                  <div className="font-medium">
                    {client?.jobTickets?.length ?? 0}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <Clock size={16} className="text-gray-400" />
                    <span>Last Booking</span>
                  </div>
                  <div>
                    {client?.jobTickets &&
                    client?.jobTickets.length > 0 &&
                    client?.jobTickets.at(0)?.bookingDate
                      ? dayjs(client?.jobTickets.at(0)?.bookingDate).format()
                      : "--"}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <Calendar size={16} className="text-gray-400" />
                    <span>Upcoming Bookings</span>
                  </div>
                  <div>
                    {client?.jobTickets && client?.jobTickets.length > 0
                      ? client?.jobTickets.filter(
                          (ticket) => ticket.status === JobStatus.SCHEDULED
                        ).length
                      : 0}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Contacts */}
        {client?.contacts && client?.contacts.length > 1 && (
          <div className="p-4">
            <h3 className="font-medium mb-3">Additional Contacts</h3>
            <div className="bg-gray-50 rounded-lg border">
              {client?.contacts.slice(1).map((contact, index) => (
                <div
                  key={index}
                  className={`p-3 flex justify-between items-center not-last:border-b`}
                >
                  <div>
                    <div className="font-medium">{contact.name}</div>
                    <div className="text-sm text-gray-500">
                      {contact.position}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Mail size={14} />
                      <span>{contact.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 mt-1">
                      <Phone size={14} />
                      <span>{contact.phone}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {client?.notes && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-medium">Notes</h3>
              <button
                className="text-sm text-blue-600 hover:text-blue-800"
                onClick={() => setShowNoteForm(!showNoteForm)}
              >
                {showNoteForm ? "Cancel" : "Add Note"}
              </button>
            </div>

            {showNoteForm && (
              <div className="bg-gray-50 border rounded-lg p-3 mb-3">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Note
                    </label>
                    <textarea
                      rows={3}
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Type your note here..."
                      value={noteContent}
                      onChange={(e) => setNoteContent(e.target.value)}
                      disabled={isCreatingNote}
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      className="px-3 py-2 border rounded-lg hover:bg-gray-50 text-sm"
                      onClick={() => {
                        setShowNoteForm(false);
                        setNoteContent("");
                      }}
                      disabled={isCreatingNote}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 text-sm"
                      onClick={handleAddNote}
                      disabled={isCreatingNote || !noteContent.trim()}
                    >
                      {isCreatingNote ? "Saving..." : "Save Note"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {client?.notes.length > 0 ? (
              <div className="space-y-2">
                {client?.notes.map((note, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg border p-3">
                    <div className="text-sm">{note.content}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {getClientName(note.author)} -{" "}
                      {new Date(note.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-sm">
                No notes for this client
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="p-4 flex justify-end space-x-3">
          <ListClientInventory
            client={selectedClient}
            onClose={() => setSelectedClient(null)}
          />
          <Button
            disabled={
              !inventoryData || inventoryData.meta.totalItems === 0
            }
            onClick={handleExportClientInventory}
            loading={exportMutation.isPending}
            className="flex items-center space-x-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <Download size={16} />
            <span>Export Inventory</span>
          </Button>
          <Button
            disabled={
              !inventoryData || inventoryData.meta.totalItems === 0
            }
            onClick={() => client && setSelectedClient(client)}
            className="flex items-center space-x-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <Eye size={16} />
            <span>View Inventory</span>
          </Button>
          <Button
            disabled
            className="flex items-center space-x-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <Calendar size={16} />
            <span>View History</span>
          </Button>
          <Button
            onClick={() => client?.id && openProfileModal(client.id)}
            className="flex items-center space-x-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <Eye size={16} />
            <span>View Profile</span>
          </Button>
          <Button
            onClick={() => openClientModal(client?.id)}
            className="flex items-center space-x-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <Edit size={16} />
            <span>Edit Client</span>
          </Button>
          {isSuperAdmin && (
            <Button
              type="primary"
              danger
              onClick={handleDeleteClient}
              className="flex items-center px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
              loading={deleteMutation.isPending}
            >
              <Trash2 size={16} />
              <span>Delete</span>
            </Button>
          )}
          <Button
            onClick={() => openBookingModal({ clientId: client?.id })}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus size={16} />
            <span>New Booking</span>
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteClientModal
        client={client ?? null}
        inventoryData={inventoryData}
        isDeleteModalVisible={isDeleteModalVisible}
        setIsDeleteModalVisible={setIsDeleteModalVisible}
        confirmDelete={confirmDelete}
        deleteMutation={deleteMutation}
      />

      {/* Activity Modal */}
      <ActivityModal
        client={client ?? null}
        isOpen={showActivityModal}
        onClose={() => setShowActivityModal(false)}
      />
    </Spin>
  );
};

const getStatusBadge = (status: string) => {
  const bgColor =
    status === ClientStatus.ACTIVE
      ? "bg-green-100 text-green-800"
      : "bg-red-100 text-red-800";

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};
const getClientTypeBadge = (type: string) => {
  let bgColor = "";

  switch (type) {
    case ClientType.GALLERY:
      bgColor = "bg-blue-100 text-blue-800";
      break;
    case "MUSEUM":
      bgColor = "bg-purple-100 text-purple-800";
      break;
    case ClientType.COLLECTOR:
      bgColor = "bg-green-100 text-green-800";
      break;
    case "ARTIST":
      bgColor = "bg-yellow-100 text-yellow-800";
      break;
    case ClientType.OTHER:
      bgColor = "bg-gray-100 text-gray-800";
      break;
    default:
      bgColor = "bg-gray-100 text-gray-800";
  }

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor}`}>
      {type.charAt(0).toUpperCase() + type.slice(1)}
    </span>
  );
};

const ListClientInventory = ({
  client,
  onClose,
}: {
  client: TClient | null;
  onClose: () => void;
}) => {
  const { filters } = useInventoryStore();
  const [page, setPage] = useState(1);
  const { data: inventories, isLoading } = useGetInventories(
    {
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
      clientId: client?.id ?? "",
    },
    {
      enabled: !!client,
    }
  );

  return (
    <Modal
      open={!!client?.id}
      onCancel={onClose}
      footer={null}
      width={"90%"}
      style={{ maxWidth: "90vw" }}
      bodyStyle={{ overflowX: "auto" }}
      title={`Inventory of ${client?.companyName}`}
    >
      <Spin spinning={isLoading} size="large">
        <div style={{ minWidth: "600px" }}>
          {" "}
          <TableInventory
            inventories={inventories?.items}
            pagination={{
              current: page,
              pageSize: 10,
              total: inventories?.meta.totalItems ?? 0,
              onChange: (page) => setPage(page),
            }}
          />
        </div>
      </Spin>
    </Modal>
  );
};

const ActivityModal = ({
  client,
  isOpen,
  onClose,
}: {
  client: TClient | null;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const navigate = useNavigate();

  // Fetch all bookings for this client (including affiliated)
  const { data: clientBookings } = useGetClientBookings(client?.id || '', {
    page: 1,
    pageSize: 100,
  });

  // Flatten all services from all bookings
  const allServices = useMemo(() => {
    const bookings = clientBookings?.items || [];
    if (bookings.length === 0) {
      return [];
    }

    const services = bookings.flatMap((booking: any) => {
      const serviceTickets = booking.serviceTicket || [];
      return serviceTickets.map((service: any) => ({
        ...service,
        bookingRef: booking.referenceNumber,
        bookingId: booking.id,
        bookingDate: booking.bookingDate,
        bookingStatus: booking.status,
        jobTicket: booking, // Pass the full booking object for accessing project
      }));
    });

    return services;
  }, [clientBookings]);

  // Separate services by client relationship
  const { upcomingServices, pastServices, affiliatedServices } = useMemo(() => {
    const upcoming: any[] = [];
    const past: any[] = [];
    const affiliated: any[] = [];

    allServices.forEach((service) => {
      // Check if this is an affiliated service (client is service client, not main booking client)
      const isAffiliated = service.clientId === client?.id && service.jobTicket.clientId !== client?.id;

      if (isAffiliated) {
        affiliated.push(service);
      } else {
        // Main booking client services
        if (
          service.status === JobStatus.DRAFT ||
          service.status === JobStatus.SCHEDULED ||
          service.status === JobStatus.IN_PROGRESS
        ) {
          upcoming.push(service);
        } else if (
          service.status === JobStatus.COMPLETED ||
          service.status === JobStatus.CANCELLED
        ) {
          past.push(service);
        }
      }
    });

    // Sort past and affiliated by date descending (most recent first)
    past.sort((a, b) => {
      const dateA = a.serviceDatetime ? new Date(a.serviceDatetime).getTime() : 0;
      const dateB = b.serviceDatetime ? new Date(b.serviceDatetime).getTime() : 0;
      return dateB - dateA;
    });

    affiliated.sort((a, b) => {
      const dateA = a.serviceDatetime ? new Date(a.serviceDatetime).getTime() : 0;
      const dateB = b.serviceDatetime ? new Date(b.serviceDatetime).getTime() : 0;
      return dateB - dateA;
    });

    return { upcomingServices: upcoming, pastServices: past, affiliatedServices: affiliated };
  }, [allServices, client?.id]);

  const getServiceTypeIcon = (type: ServiceType) => {
    switch (type) {
      case ServiceType.DELIVERY:
        return <Truck size={14} />;
      case ServiceType.COLLECTION:
        return <Package size={14} />;
      default:
        return <Calendar size={14} />;
    }
  };

  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case JobStatus.DRAFT:
        return "orange";
      case JobStatus.SCHEDULED:
        return "blue";
      case JobStatus.IN_PROGRESS:
        return "cyan";
      case JobStatus.COMPLETED:
        return "green";
      case JobStatus.CANCELLED:
        return "red";
      default:
        return "default";
    }
  };

  const handleRowClick = (record: any) => {
    const bookingId = record.bookingId;
    if (bookingId) {
      navigate(`/bookings/${bookingId}?tab=services&serviceId=${record.id}`);
      onClose();
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "serviceDatetime",
      key: "serviceDatetime",
      render: (date: string) => (date ? dayjs(date).format('ddd, MMM D, YYYY') : " - "),
    },
    {
      title: "Service Ref",
      dataIndex: "serial",
      key: "serial",
      render: (text: string) => <Text strong>{text || " - "}</Text>,
    },
    {
      title: "Project",
      dataIndex: "jobTicket",
      key: "project",
      render: (jobTicket: any) => jobTicket?.project?.name || " - ",
    },
    {
      title: "Type",
      dataIndex: "serviceType",
      key: "serviceType",
      render: (text: ServiceType) => (
        <div className="flex items-center gap-1">
          {getServiceTypeIcon(text)}
          <span>{text ? capitalize(text) : " - "}</span>
        </div>
      ),
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: (serviceClient: any) => serviceClient?.companyName || " - ",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (location: any) => {
        if (!location) return " - ";
        const city = location.city || "";
        const state = location.state || "";
        if (city && state) return `${city}, ${state}`;
        if (city) return city;
        if (state) return state;
        return " - ";
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: JobStatus) => (
        <Tag color={getStatusColor(status)}>{status ? capitalize(status) : " - "}</Tag>
      ),
    },
  ];

  // Columns for affiliated services - includes Booking Client column
  const affiliatedColumns = [
    {
      title: "Date",
      dataIndex: "serviceDatetime",
      key: "serviceDatetime",
      render: (date: string) => (date ? dayjs(date).format('ddd, MMM D, YYYY') : " - "),
    },
    {
      title: "Service Ref",
      dataIndex: "serial",
      key: "serial",
      render: (text: string) => <Text strong>{text || " - "}</Text>,
    },
    {
      title: "Booking Client",
      dataIndex: "jobTicket",
      key: "bookingClient",
      render: (jobTicket: any) => jobTicket?.client?.companyName || " - ",
    },
    {
      title: "Project",
      dataIndex: "jobTicket",
      key: "project",
      render: (jobTicket: any) => jobTicket?.project?.name || " - ",
    },
    {
      title: "Type",
      dataIndex: "serviceType",
      key: "serviceType",
      render: (text: ServiceType) => (
        <div className="flex items-center gap-1">
          {getServiceTypeIcon(text)}
          <span>{text ? capitalize(text) : " - "}</span>
        </div>
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (location: any) => {
        if (!location) return " - ";
        const city = location.city || "";
        const state = location.state || "";
        if (city && state) return `${city}, ${state}`;
        if (city) return city;
        if (state) return state;
        return " - ";
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: JobStatus) => (
        <Tag color={getStatusColor(status)}>{status ? capitalize(status) : " - "}</Tag>
      ),
    },
  ];

  const tabItems = [
    {
      key: "upcoming",
      label: `Upcoming Services (${upcomingServices.length})`,
      children: (
        <Table
          bordered
          dataSource={upcomingServices}
          columns={columns}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: "pointer" },
          })}
          pagination={upcomingServices.length > 10 ? { pageSize: 10 } : false}
          size="small"
          scroll={{ x: 800 }}
          locale={{
            emptyText: "No upcoming services found for this client",
          }}
        />
      ),
    },
    {
      key: "past",
      label: `Past Services (${pastServices.length})`,
      children: (
        <Table
          bordered
          dataSource={pastServices}
          columns={columns}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: "pointer" },
          })}
          pagination={pastServices.length > 10 ? { pageSize: 10 } : false}
          size="small"
          scroll={{ x: 800 }}
          locale={{
            emptyText: "No past services found for this client",
          }}
        />
      ),
    },
    {
      key: "affiliated",
      label: `Affiliated (${affiliatedServices.length})`,
      disabled: affiliatedServices.length === 0,
      children: (
        <>
          <Alert
            message="Affiliated Services"
            description="These service tickets were completed as part of another client's booking where this client was listed as a service client."
            type="info"
            showIcon
            className="mb-4"
            style={{ backgroundColor: "#f3e8ff", borderColor: "#d8b4fe" }}
          />
          <Table
            bordered
            dataSource={affiliatedServices}
            columns={affiliatedColumns}
            rowKey="id"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: "pointer" },
            })}
            pagination={affiliatedServices.length > 10 ? { pageSize: 10 } : false}
            size="small"
            scroll={{ x: 800 }}
            locale={{
              emptyText: "No affiliated services found for this client",
            }}
          />
        </>
      ),
    },
  ];

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={"90%"}
      style={{ maxWidth: "90vw" }}
      title={`All Services - ${client?.companyName || getClientName(client ?? undefined)}`}
    >
      <Tabs defaultActiveKey="upcoming" items={tabItems} />
    </Modal>
  );
};

const DeleteClientModal = ({
  client,
  inventoryData,
  isDeleteModalVisible,
  setIsDeleteModalVisible,
  confirmDelete,
  deleteMutation,
}: {
  client: TClient | null;
  inventoryData: any;
  isDeleteModalVisible: boolean;
  setIsDeleteModalVisible: (visible: boolean) => void;
  confirmDelete: () => void;
  deleteMutation: UseMutationResult<void, Error, string>;
}) => {
  const hasActiveInventory = inventoryData?.items?.some(
    (inv: any) =>
      inv.status === InventoryStatusCode.AVAILABLE_LIVE ||
      inv.status === InventoryStatusCode.AVAILABLE_ON_HAND
  );

  const hasInventory =
    inventoryData && inventoryData.meta?.totalItems > 0;
  const exportMutation = useExportInventory();
  const { success, error } = useNotification();

  const handleExportInventory = () => {
    if (!client) return;

    exportMutation.mutate(
      { clientId: client.id },
      {
        onSuccess: () => {
          success({
            message: "Export successful",
            description: `Inventory data for ${
              client.companyName || getClientName(client ?? undefined)
            } has been exported to CSV`,
          });
        },
        onError: (err) => {
          error({
            message: "Export failed",
            description:
              err.message || "There was an error exporting the inventory data",
          });
        },
      }
    );
  };

  return (
    <Modal
      title="Delete Client"
      open={isDeleteModalVisible}
      onCancel={() => setIsDeleteModalVisible(false)}
      footer={[
        <Button key="cancel" onClick={() => setIsDeleteModalVisible(false)}>
          Cancel
        </Button>,
        hasInventory && (
          <Button
            key="export"
            onClick={handleExportInventory}
            loading={exportMutation.isPending}
          >
            <Download size={16} className="mr-1" />
            Export Inventory
          </Button>
        ),
        <Button
          key="delete"
          type="primary"
          danger
          onClick={confirmDelete}
          loading={deleteMutation.isPending}
          disabled={hasActiveInventory}
        >
          Delete
        </Button>,
      ]}
    >
      <p>
        Are you sure you want to delete{" "}
        {client?.companyName || getClientName(client ?? undefined)}?
      </p>
      <p className="text-red-500 mt-2">This action cannot be undone.</p>

      {hasInventory && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          {hasActiveInventory ? (
            <p className="text-yellow-700">
              This client has active inventory items with status "Available
              Live" or "Available on Hand" and cannot be deleted. Please remove
              or change status of all active inventory items first.
            </p>
          ) : (
            <div className="text-yellow-700">
              <p className="font-medium mb-2">⚠️ Warning</p>
              <p className="mb-2">
                This client has inventory items that will be permanently
                deleted.
              </p>
              <p className="mb-2">
                We highly recommend exporting the inventory data before
                proceeding with deletion. A backup copy will be automatically
                saved on the server, but you should export a local copy as well.
              </p>
              <p>
                Use the "Export Inventory" button below to download a copy
                before deleting.
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};
